import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState, useEffect } from 'react';
import { useSearch, useNavigate } from '@tanstack/react-location';
import SpinnerIcon from './icon/SpinnerIcon';
import { apiRawFetch } from '../client';

const AcceptInvitation = () => {
    const search = useSearch();
    const navigate = useNavigate();
    const token = search.token as string | undefined;

    const [invitationValid, setInvitationValid] = useState<boolean | null>(null);
    const [invitationData, setInvitationData] = useState<{
        email: string;
        name: string;
        expiresAt: string;
    } | null>(null);

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isVerifying, setIsVerifying] = useState(true);

    useEffect(() => {
        const verifyToken = async () => {
            if (!token) {
                setInvitationValid(false);
                setIsVerifying(false);
                return;
            }

            try {
                const response = await apiRawFetch(`api/invitations/verify/${token}`, 'GET');

                if (response.ok) {
                    const data = await response.json();
                    if (data.valid) {
                        setInvitationValid(true);
                        setInvitationData({
                            email: data.email,
                            name: data.name,
                            expiresAt: data.expiresAt,
                        });
                    } else {
                        setInvitationValid(false);
                    }
                } else {
                    setInvitationValid(false);
                }
            } catch (err) {
                setInvitationValid(false);
            } finally {
                setIsVerifying(false);
            }
        };

        verifyToken();
    }, [token]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        // Validate password
        if (password.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (!token) {
            setError('Invalid invitation token');
            return;
        }

        setIsLoading(true);

        try {
            const response = await apiRawFetch('api/invitations/accept', 'POST', {
                token,
                password,
            });

            if (response.ok) {
                const data = await response.json();
                localStorage.setItem('token', data.token);
                // Redirect to main page
                navigate({ to: '/' });
                // eslint-disable-next-line no-restricted-globals
                location.reload();
            } else {
                const errorText = await response.text();
                setError(errorText || 'Failed to accept invitation. The invitation may have expired or been revoked.');
            }
        } catch (err) {
            setError(`Failed to accept invitation. Please try again. ${err}`);
        } finally {
            setIsLoading(false);
        }
    };

    if (isVerifying) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Box display="flex" justifyContent="center">
                    <SpinnerIcon />
                </Box>
            </Container>
        );
    }

    if (!invitationValid) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Card>
                    <CardContent>
                        <Typography variant="h5" component="h1" gutterBottom>
                            Invalid Invitation
                        </Typography>
                        <Alert severity="error" sx={{ mb: 2 }}>
                            This invitation link is invalid, has expired, or has already been used.
                        </Alert>
                        <Button variant="contained" onClick={() => navigate({ to: '/' })}>
                            Go to Login
                        </Button>
                    </CardContent>
                </Card>
            </Container>
        );
    }

    return (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
            <Card>
                <CardContent>
                    <Typography variant="h5" component="h1" gutterBottom>
                        Accept Invitation
                    </Typography>
                    <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                        Welcome {invitationData?.name}! You've been invited to join MushRoom Property Management.
                        Please set your password to complete your registration.
                    </Typography>

                    <Box component="form" onSubmit={handleSubmit} display="flex" flexDirection="column" gap={2}>
                        <TextField
                            label="Email"
                            type="email"
                            value={invitationData?.email || ''}
                            disabled
                            fullWidth
                        />
                        <TextField
                            label="Password"
                            type="password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            required
                            disabled={isLoading}
                            fullWidth
                            helperText="Minimum 8 characters"
                        />
                        <TextField
                            label="Confirm Password"
                            type="password"
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            required
                            disabled={isLoading}
                            fullWidth
                        />
                        {error && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                                {error}
                            </Alert>
                        )}
                        <Button type="submit" variant="contained" disabled={isLoading} sx={{ mt: 2 }}>
                            {isLoading ? 'Creating Account...' : 'Accept Invitation'}
                        </Button>
                    </Box>

                    <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                        Invitation expires on {invitationData && new Date(invitationData.expiresAt).toLocaleDateString()}
                    </Typography>
                </CardContent>
            </Card>
        </Container>
    );
};

export default AcceptInvitation;
